<template>
  <div
    v-if="loggedIn"
    class="start-view"
  >
    <div class="start-view__illustration">
      <BikeSetupStart v-if="hasStartPosition" />
    </div>

    <div class="start-view__content">
      <TitleBar :title="t('steps.start')">
        <BfButton
          icon="/icons/52-Arrows-Diagrams/01-Arrows/arrow-right-1.svg"
          :icon-position="ButtonIconPosition.Right"
          :state="connected ? ButtonState.Enabled : ButtonState.Disabled"
          @click="next"
        >
          {{ t('common.next') }}
        </BfButton>
      </TitleBar>

      <BfLoader v-if="loading" />
      <div v-else>
        <BfInfoCard
          v-if="!connected"
          :title="t('start.notConnected.index')"
        >
          {{ t('start.notConnected.content') }}
        </BfInfoCard>

        <div class="start-view__setup">
          <StartSetup v-if="hasStartPosition" />
          <p v-else>{{ t('start.noStartPosition') }}</p>
        </div>
      </div>
    </div>
  </div>
  <div
    v-else
    class="start-view start-view--error"
  >
    <BfLoader v-if="loading && !error" />
    <div v-else>
      <h1>{{ t('start.session.unknown.title') }}</h1>
      <p>{{ t('start.session.unknown.content') }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useBikeStore } from '@/store/bikeStore'
import { usePositionStore } from '@/store/positionStore'
import { useMeasurementStore } from '@/store/measurementStore'
import { useUserStore } from '@/store/userStore'
import { useMqttStore } from '@/store/mqttStore'
import {
  BfButton,
  BfInfoCard,
  BfLoader,
  ButtonState,
  ButtonIconPosition,
} from '@finetic/bikefitting-ui'
import { useI18n } from 'vue-i18n'
import { getBikeCategory, getHoodsTypeFromCategory } from '@/utils/utils'
import { PreviewImage } from '@/types/preview'
import { BikeHoodsType } from '@/types/bike'
import TitleBar from '@/components/layout/TitleBar.vue'
import BikeSetupStart from '@/components/preview/BikeSetupStart.vue'
import StartSetup from '@/components/setup/StartSetup.vue'
import cranksetService from '@/service/cranksetService'
import handlebarService from '@/service/handlebarService'
import saddleService from '@/service/saddleService'
import pedalService from '@/service/pedalService'

const { t } = useI18n()
const router = useRouter()
const bikeStore = useBikeStore()
const positionStore = usePositionStore()
const measurementStore = useMeasurementStore()
const userStore = useUserStore()
const mqttStore = useMqttStore()
const { loggedIn, customerId, dealerId, sessionId } = storeToRefs(userStore)
const { bikeData, bikeNo, bikeHoodsType, bikePreview } = storeToRefs(bikeStore)
const { startPosition } = storeToRefs(positionStore)
const { latestMeasurement } = storeToRefs(measurementStore)
const { connected } = storeToRefs(mqttStore)
const { checkLogin } = userStore

const loading = ref(true)
const error = ref(false)

const averageCrankset = ref()
const averageHandlebar = ref()
const averageSaddle = ref()
const averagePedal = ref()

const fetchAverages = async () => {
  averageCrankset.value = cranksetService.fetchAverageCrankset()
  averageHandlebar.value = await handlebarService.fetchAverageHandlebar({
    sessionId: sessionId?.value,
    bikeHoodsType: bikeHoodsType?.value,
  })
  averageSaddle.value = await saddleService.fetchAverageSaddle({
    sessionId: sessionId?.value,
  })
  averagePedal.value = await pedalService.fetchAveragePedal({
    sessionId: sessionId?.value,
  })
}

const createInitialMeasurement = async () => {
  measurementStore.create({
    crankset: {
      active: averageCrankset.value,
    },
    handlebar: {
      active: averageHandlebar.value,
      x: startPosition.value.handlebarX,
      y: startPosition.value.handlebarY,
    },
    saddle: {
      active: averageSaddle.value,
      x: startPosition.value.saddleX,
      y: startPosition.value.saddleY,
    },
    pedal: {
      active: averagePedal.value,
    },
  })
}

const getInitialDistance = () => {
  bikeStore.updateXY(
    customerId.value,
    dealerId.value,
    sessionId.value,
    latestMeasurement.value,
    false,
  )
}

const login = () => {
  if (router.currentRoute.value.query.bikeNo) {
    bikeNo.value = Number.parseInt(
      router.currentRoute.value.query.bikeNo as string,
    )
  }

  if (router.currentRoute.value.query.customerId) {
    customerId.value = router.currentRoute.value.query.customerId as string
  }

  if (router.currentRoute.value.query.sessionId) {
    checkLogin(router.currentRoute.value.query.sessionId as string)
      .then(() => {
        error.value = false

        bikeStore
          .retrieveBikes(customerId.value, dealerId.value, sessionId.value)
          .then(async () => {
            loading.value = false

            if (bikeData && bikeData.value) {
              startPosition.value = {
                saddleX: bikeData.value?.SF_SADDLE_CENTER_X,
                saddleY: bikeData.value?.SF_SADDLE_CENTER_Y,
                handlebarX: bikeData.value?.SF_HANDLEBAR_CENTER_X * -1,
                handlebarY: bikeData.value?.SF_HANDLEBAR_CENTER_Y,
              }

              const bikeCategory = getBikeCategory(
                bikeData.value?.MAIN_BIKETYPE_NAME,
                bikeData.value?.SUB_BIKETYPE_NAME,
              )
              bikeHoodsType.value = getHoodsTypeFromCategory(bikeCategory)

              switch (bikeHoodsType.value) {
                case BikeHoodsType.DROPBAR:
                  bikePreview.value = PreviewImage.DROPBAR_BIKE
                  break
                case BikeHoodsType.TRIBAR:
                  bikePreview.value = PreviewImage.TRIBAR_BIKE
                  break
                case BikeHoodsType.FLATBAR:
                  bikePreview.value = PreviewImage.FLATBAR_BIKE
                  break
                default:
                  bikePreview.value = PreviewImage.DROPBAR_BIKE
              }
            }

            if (!latestMeasurement.value) {
              await fetchAverages()
              createInitialMeasurement()
              createInitialMeasurement()
              getInitialDistance()
            }
          })
      })
      .catch(() => {
        loading.value = false
        error.value = true
      })
  } else {
    loading.value = false
  }
}

const hasStartPosition = computed(
  () =>
    startPosition?.value?.handlebarX &&
    startPosition?.value?.handlebarY &&
    startPosition?.value?.saddleX &&
    startPosition?.value?.saddleY,
)

if (
  (router.currentRoute.value.query.customerId &&
    router.currentRoute.value.query.customerId !== customerId.value) ||
  (router.currentRoute.value.query.bikeNo &&
    router.currentRoute.value.query.bikeNo !== bikeNo.value)
) {
  userStore.logout()
}

if (loggedIn.value) {
  loading.value = false
} else {
  login()
}

const next = () => {
  router.push({ name: 'setup' })
}
</script>

<style lang="scss">
.start-view {
  display: flex;
  height: 100%;

  &--error {
    justify-content: center;
    align-items: center;
    padding: 2.4rem;
    text-align: center;
    background-color: #fbfbfb;
  }

  &__illustration {
    flex: 1 0 40%;
    background-color: #f5f5f5;
    padding: 2.4rem;
    overflow-y: auto;
    scrollbar-gutter: stable;
  }

  &__content {
    flex: 1 0 60%;
    background-color: #fbfbfb;
    padding: 2.4rem;
    overflow-y: auto;
    scrollbar-gutter: stable;
  }

  &__setup {
    margin-top: 3.2rem;
  }
}
</style>
